/* ====================================================================
   Premium Cart Drawer — assets/premium-cart-drawer.js
   Single IIFE exposing window.PCD (also aliased to window.cartDrawer).
   ==================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'premium-cart-drawer';

  var PCD = {
    drawer: null,
    scroll: null,
    _refreshing: false,
    _pendingHighlight: null,

    init: function () {
      this.drawer = document.getElementById('pcd-drawer');
      this.scroll = document.getElementById('pcd-scroll');
      if (!this.drawer) return;

      this.removeThemeCart();
      this.bindDrawerEvents();
      this.bindGlobalEvents();
      this.updateCartBubble();
      this.clearStaleCouponIfAny();
      this.shuffleUpsell();

      // Deferred open when returning from /cart redirect
      try {
        if (sessionStorage.getItem('pcd-open-on-load') === '1') {
          sessionStorage.removeItem('pcd-open-on-load');
          PCD.openDrawer();
        }
      } catch (e) { /* ignore */ }
    },

    // ---------- Kill Shrine Pro cart UI ---------------------------------
    removeThemeCart: function () {
      ['cart-notification', 'cart-popup'].forEach(function (tag) {
        document.querySelectorAll(tag).forEach(function (el) { el.remove(); });
      });
      // Also remove cart-drawer EXCEPT our own drawer
      document.querySelectorAll('cart-drawer').forEach(function (el) {
        if (el.id !== 'pcd-drawer') el.remove();
      });
    },

    // ---------- Event wiring --------------------------------------------
    bindDrawerEvents: function () {
      var self = this;
      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest('[data-pcd-close]')) { e.preventDefault(); return self.closeDrawer(); }

        var qtyBtn = e.target.closest('[data-pcd-qty]');
        if (qtyBtn) { e.preventDefault(); return self.changeQty(qtyBtn); }

        var trashBtn = e.target.closest('[data-pcd-remove]');
        if (trashBtn) {
          e.preventDefault();
          return self.showRemoveNudge(
            parseInt(trashBtn.dataset.line, 10),
            trashBtn.dataset.productTitle || 'this item'
          );
        }

        var ddHead = e.target.closest('[data-pcd-toggle]');
        if (ddHead) { e.preventDefault(); return self.toggleDropdown(ddHead); }

        var applyBtn = e.target.closest('[data-pcd-apply-offer]');
        if (applyBtn) { e.preventDefault(); return self.applyCoupon(applyBtn.dataset.code); }

        var removeCoupon = e.target.closest('[data-pcd-remove-coupon]');
        if (removeCoupon) {
          e.preventDefault();
          var codeToRemove = removeCoupon.getAttribute('data-code') || '';
          if (!codeToRemove) {
            var ownerRow = removeCoupon.closest('.pcd-offer.is-applied');
            if (ownerRow) codeToRemove = ownerRow.getAttribute('data-applied-code') || '';
          }
          return self.removeCoupon(codeToRemove);
        }

        var upsellAdd = e.target.closest('[data-pcd-upsell-add]');
        if (upsellAdd) { e.preventDefault(); return self.addUpsell(upsellAdd.dataset.variantId, upsellAdd); }

        var upsellLink = e.target.closest('[data-pcd-upsell-link]');
        if (upsellLink) {
          // Let the browser navigate; just close the drawer first so the
          // product page is visible without the drawer overlay still on top.
          self.closeDrawer();
          return;
        }

        var checkout = e.target.closest('[data-pcd-checkout]');
        if (checkout) { e.preventDefault(); return self.proceedToCheckout(); }
      });

      this.drawer.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-pcd-coupon-form]');
        if (!form) return;
        e.preventDefault();
        var input = form.querySelector('input[name="code"]');
        var code = input ? input.value.trim() : '';
        if (code) self.applyCoupon(code);
      });
    },

    bindGlobalEvents: function () {
      var self = this;
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen()) self.closeDrawer();
      });
    },

    // ---------- Drawer state --------------------------------------------
    openDrawer: function (opts) {
      if (!this.drawer) return;
      opts = opts || {};
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      var self = this;
      var doHighlight = function () {
        self.updateCartBubble();
        if (opts.highlightVariantId != null) {
          self.highlightItem(opts.highlightVariantId);
        }
      };

      // Callers that just applied fresh section HTML (addItem, addBundleItems,
      // sticky cart, ATC Pro Plus) pass skipRefresh:true to avoid an extra
      // ~300ms round-trip that was doubling the perceived Add-to-Cart latency.
      if (opts.skipRefresh) {
        doHighlight();
      } else {
        this.refreshCart().then(doHighlight);
      }
    },

    closeDrawer: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.stopUpsellAutoScroll();
    },

    isOpen: function () {
      return !!(this.drawer && this.drawer.classList.contains('is-open'));
    },

    highlightItem: function (variantId) {
      if (variantId == null) return;
      var el = this.drawer.querySelector('[data-variant-id="' + variantId + '"]');
      if (!el) return;
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); }
      el.classList.add('pcd-item--just-added');
      setTimeout(function () { el.classList.remove('pcd-item--just-added'); }, 1200);
    },

    // ---------- Direct DOM apply from a /cart/*.js response --------------
    // This gives INSTANT feedback from the authoritative cart JSON without
    // waiting on the section-render API (which can race with server writes).
    _formatPaise: function (paise) {
      var rupees = (paise || 0) / 100;
      var rounded = Math.round(rupees * 100) / 100;
      var str;
      if (rounded === Math.floor(rounded)) {
        str = Math.floor(rounded).toLocaleString('en-IN');
      } else {
        str = rounded.toFixed(2);
        var parts = str.split('.');
        parts[0] = parseInt(parts[0], 10).toLocaleString('en-IN');
        str = parts.join('.');
      }
      return '₹' + str;
    },

    applyCartToDrawer: function (cart) {
      if (!cart || !this.drawer) return;
      var self = this;

      // --- Detect ALL applied cart-level discounts (multi-coupon + auto) -
      // This drives BOTH the coupon-dropdown chip AND per-line badges.
      // Each entry: { code: 'HELLO', amount: 10000, isAuto: false }
      var appliedList = [];
      var seenApplied = {};
      function pushApplied(title, amount, isAuto) {
        var upper = (title || '').toUpperCase();
        if (!upper || seenApplied[upper]) return;
        seenApplied[upper] = true;
        appliedList.push({ code: upper, amount: amount || 0, isAuto: !!isAuto });
      }
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        if (d.title && (d.total_allocated_amount || 0) > 0) {
          pushApplied(d.title, d.total_allocated_amount, d.type === 'automatic');
        }
      });
      (cart.discount_applications || []).forEach(function (d) {
        if (d.title && (d.total_allocated_amount || 0) > 0 &&
            (d.type === 'discount_code' || d.type === 'automatic')) {
          pushApplied(d.title, d.total_allocated_amount, d.type === 'automatic');
        }
      });

      var appliedCode = appliedList.length ? appliedList[0].code : '';
      var appliedAmount = appliedList.reduce(function (s, a) { return s + (a.amount || 0); }, 0);

      // Last-resort fallback: derive cart-level discount from totals when
      // Shopify's change.js response lags on populating discount_applications.
      if (!appliedList.length) {
        var cartLevelFromTotals = Math.max(0, (cart.items_subtotal_price || 0) - (cart.total_price || 0));
        if (cartLevelFromTotals > 0) {
          appliedCode = 'DISCOUNT';
          appliedAmount = cartLevelFromTotals;
          appliedList.push({ code: 'DISCOUNT', amount: cartLevelFromTotals, isAuto: true });
        }
      }

      var appliedSet = {};
      appliedList.forEach(function (a) { appliedSet[a.code] = a; });

      var hasZeroCode = (cart.cart_level_discount_applications || []).some(function (d) {
        return d.title && (d.total_allocated_amount || 0) === 0 && !appliedSet[(d.title || '').toUpperCase()];
      }) || (cart.discount_applications || []).some(function (d) {
        return d.type === 'discount_code' && d.title && (d.total_allocated_amount || 0) === 0 && !appliedSet[(d.title || '').toUpperCase()];
      });

      // Basis for per-line proportional allocation of the cart-level discount.
      var itemsBasis = (cart.items || []).reduce(function (sum, it) {
        return sum + (it.final_line_price || 0);
      }, 0);

      // --- Line-level updates: qty + effective line price + badge -----
      // Look up rows by data-key (via JS getAttribute to avoid CSS escaping
      // issues with colons in line-item keys), then fall back to forloop index.
      var allRows = Array.prototype.slice.call(this.drawer.querySelectorAll('.pcd-cart-item'));
      (cart.items || []).forEach(function (item, idx) {
        var line = idx + 1;
        var qtyEl = self.drawer.querySelector('[data-qty-val="' + line + '"]');
        if (qtyEl) qtyEl.textContent = item.quantity;

        var row = null;
        for (var i = 0; i < allRows.length; i++) {
          if (allRows[i].getAttribute('data-key') === item.key) { row = allRows[i]; break; }
        }
        if (!row) row = allRows[idx] || null;
        if (!row) return;

        // Proportional share of cart-level discount allocated to this line.
        var lineShare = 0;
        if (appliedAmount > 0 && itemsBasis > 0) {
          lineShare = Math.round(appliedAmount * item.final_line_price / itemsBasis);
        }
        var effectiveLinePrice = Math.max(0, item.final_line_price - lineShare);

        var priceEl = row.querySelector('.pcd-cart-item__price');
        if (priceEl) priceEl.textContent = self._formatPaise(effectiveLinePrice);

        // MRP comes from a data-attribute stamped at Liquid render time —
        // Shopify's /cart/change.js JSON doesn't reliably expose compare_at_price,
        // so we scale the unit MRP locally by the new quantity.
        var unitMRP = parseInt(row.getAttribute('data-unit-mrp'), 10) || 0;
        var lineMRP = 0;
        if (unitMRP > 0) {
          lineMRP = unitMRP * item.quantity;
        } else if (item.original_line_price > effectiveLinePrice) {
          lineMRP = item.original_line_price;
        }
        var hasMRP = lineMRP > effectiveLinePrice;

        var mrpEl = row.querySelector('.pcd-cart-item__mrp');
        if (mrpEl) {
          if (hasMRP) {
            mrpEl.textContent = self._formatPaise(lineMRP);
            mrpEl.style.display = '';
          } else {
            mrpEl.style.display = 'none';
          }
        } else if (hasMRP) {
          // MRP element wasn't rendered at initial load (no discount then) —
          // inject one into the price row so it appears now.
          var priceRow = row.querySelector('.pcd-cart-item__price-row');
          if (priceRow) {
            var span = document.createElement('span');
            span.className = 'pcd-cart-item__mrp';
            span.textContent = self._formatPaise(lineMRP);
            priceRow.appendChild(span);
          }
        }

        var saveEl = row.querySelector('.pcd-cart-item__save');
        var pct = hasMRP ? Math.round((lineMRP - effectiveLinePrice) * 100 / lineMRP) : 0;
        if (saveEl) {
          if (hasMRP) {
            saveEl.textContent = pct + '% off';
            saveEl.style.display = '';
          } else {
            saveEl.style.display = 'none';
          }
        } else if (hasMRP) {
          var stack = row.querySelector('.pcd-cart-item__price-stack');
          if (stack) {
            var saveDiv = document.createElement('div');
            saveDiv.className = 'pcd-cart-item__save';
            saveDiv.textContent = pct + '% off';
            stack.appendChild(saveDiv);
          }
        }

        // Per-line applied-coupon badge. When multiple codes are applied we
        // show the first code + "+N" rather than stacking badges (keeps the
        // row compact).
        var appliedBadge = row.querySelector('.pcd-cart-item__applied');
        if (lineShare > 0 && appliedList.length) {
          var badgeLabel = appliedList[0].code + (appliedList.length > 1 ? ' +' + (appliedList.length - 1) : '');
          var badgeTitle = appliedList.map(function (a) { return a.code; }).join(', ') + ' applied to this line';
          var html = '🏷️ <strong>' + badgeLabel + '</strong> <span class="pcd-cart-item__applied-amt">−' +
                     self._formatPaise(lineShare) + '</span>';
          if (appliedBadge) {
            appliedBadge.innerHTML = html;
            appliedBadge.setAttribute('title', badgeTitle);
            appliedBadge.style.display = '';
          } else {
            appliedBadge = document.createElement('div');
            appliedBadge.className = 'pcd-cart-item__applied';
            appliedBadge.setAttribute('title', badgeTitle);
            appliedBadge.innerHTML = html;
            var priceStack = row.querySelector('.pcd-cart-item__price-stack');
            if (priceStack) priceStack.appendChild(appliedBadge);
          }
        } else if (appliedBadge) {
          appliedBadge.remove();
        }
      });

      // --- Header item count ------------------------------------------
      var headerCount = this.drawer.querySelector('.pcd-header__title small');
      if (headerCount) headerCount.textContent = '(' + cart.item_count + ')';

      // --- Price breakdown (dropdown) ---------------------------------
      var originalTotal = (cart.items || []).reduce(function (sum, it) { return sum + (it.original_line_price || 0); }, 0);
      var itemsSubtotal = cart.items_subtotal_price || originalTotal;
      var productDiscount = Math.max(0, originalTotal - itemsSubtotal);
      var couponDiscount = cart.total_discount || 0;
      var totalSaved = Math.max(0, originalTotal - cart.total_price);

      // Grand total needs to include shipping; we read shipping from existing DOM
      // since it's computed from section settings and stays constant per session.
      var shippingRow = this.drawer.querySelector('.pcd-price-row:nth-of-type(3) span:last-child');
      var shippingAmount = 0;
      var freeShippingNow = !!this.drawer.querySelector('.pcd-shipping-free');
      if (!freeShippingNow) {
        // Try to parse from existing shipping display; default to 0 if parse fails.
        var shipText = shippingRow ? shippingRow.textContent.replace(/[^\d]/g, '') : '';
        if (shipText) shippingAmount = parseInt(shipText, 10) * 100;
      }
      var grandTotal = cart.total_price + shippingAmount;

      var nowEl = this.drawer.querySelector('.pcd-price-dd__now');
      if (nowEl) nowEl.textContent = this._formatPaise(grandTotal);

      var saveEl = this.drawer.querySelector('.pcd-price-dd__save');
      if (saveEl) {
        if (totalSaved > 0) {
          saveEl.textContent = 'You saved ₹' + Math.round(totalSaved / 100) + '!';
          saveEl.style.display = '';
        } else {
          saveEl.style.display = 'none';
        }
      }

      // Rows inside the dropdown body
      var rows = this.drawer.querySelectorAll('.pcd-price-dd__body-inner .pcd-price-row');
      rows.forEach(function (r) {
        var labelSpan = r.querySelector('span');
        var valueSpan = r.querySelector('span:last-child, strong');
        if (!labelSpan || !valueSpan) return;
        var label = (labelSpan.textContent || '').trim().toLowerCase();
        if (label.indexOf('total mrp') === 0) {
          valueSpan.textContent = self._formatPaise(originalTotal);
        } else if (label.indexOf('discount on mrp') === 0) {
          valueSpan.textContent = '− ' + self._formatPaise(productDiscount);
          r.style.display = productDiscount > 0 ? '' : 'none';
        } else if (label.indexOf('coupon') === 0) {
          valueSpan.textContent = '− ' + self._formatPaise(couponDiscount);
          r.style.display = couponDiscount > 0 ? '' : 'none';
        } else if (label.indexOf('grand total') === 0) {
          valueSpan.textContent = self._formatPaise(grandTotal);
        }
      });

      // Saved banner (above footer)
      var bannerEl = this.drawer.querySelector('.pcd-saved-banner');
      if (bannerEl) {
        if (totalSaved > 0) {
          bannerEl.textContent = '🎉 You saved ₹' + Math.round(totalSaved / 100) + ' so far!';
          bannerEl.style.display = '';
        } else {
          bannerEl.style.display = 'none';
        }
      }

      // --- Progress bar ------------------------------------------------
      var progress = this.drawer.querySelector('[data-pcd-progress]');
      if (progress) {
        var m1 = parseFloat(progress.getAttribute('data-m1')) || 0;
        var m2 = parseFloat(progress.getAttribute('data-m2')) || 0;
        var m3 = parseFloat(progress.getAttribute('data-m3')) || 0;
        var m1Label = progress.getAttribute('data-m1-label') || '';
        var m2Label = progress.getAttribute('data-m2-label') || '';
        var m3Label = progress.getAttribute('data-m3-label') || '';
        var cartTotalRupees = cart.total_price / 100;
        var finalMilestone = m3 > 0 ? m3 : 1;
        var fillPct = cartTotalRupees >= finalMilestone ? 100 :
          Math.round(cartTotalRupees * 100 / finalMilestone);

        var fillEl = progress.querySelector('[data-pcd-progress-fill]');
        if (fillEl) fillEl.style.width = fillPct + '%';

        progress.classList.toggle('pcd-progress--unlocked', cartTotalRupees >= m3 && m3 > 0);

        var setMilestoneState = function (n, threshold, activeIfBelow) {
          var col = progress.querySelector('[data-pcd-milestone="' + n + '"]');
          if (!col) return;
          col.classList.remove('pcd-progress__col--reached', 'pcd-progress__col--active');
          if (cartTotalRupees >= threshold) col.classList.add('pcd-progress__col--reached');
          else if (activeIfBelow != null && cartTotalRupees >= activeIfBelow) col.classList.add('pcd-progress__col--active');
        };
        setMilestoneState(1, m1, null);
        setMilestoneState(2, m2, m1);
        setMilestoneState(3, m3, m2);

        var headline = progress.querySelector('[data-pcd-progress-headline]');
        if (headline) {
          if (cartTotalRupees >= m3 && m3 > 0) {
            headline.textContent = '🎉 All rewards unlocked! Enjoy your perks';
          } else {
            var nextThreshold = m1, nextLabel = m1Label;
            if (cartTotalRupees >= m1) { nextThreshold = m2; nextLabel = m2Label; }
            if (cartTotalRupees >= m2) { nextThreshold = m3; nextLabel = m3Label; }
            var remaining = Math.max(0, Math.round(nextThreshold - cartTotalRupees));
            headline.textContent = 'Add ₹' + remaining + ' more to unlock ' + nextLabel;
          }
        }
      }

      // --- Sync the coupon dropdown chip with current cart state ------
      // so coupon add/remove/qty changes show up instantly, without waiting
      // for the ~500ms section-render refresh.
      var couponDD = this.drawer.querySelector('[data-pcd-dropdown="coupon"]');
      if (couponDD) {
        var sub = couponDD.querySelector('.pcd-dropdown__sub');
        var badge = couponDD.querySelector('.pcd-dropdown__badge');
        var bodyInner = couponDD.querySelector('.pcd-dropdown__body-inner');

        // Update head badge (sum of all applied amounts).
        if (appliedAmount > 0) {
          if (badge) {
            badge.textContent = self._formatPaise(appliedAmount) + ' OFF';
            badge.style.display = '';
          } else {
            var right = couponDD.querySelector('.pcd-dropdown__right');
            if (right) {
              var b = document.createElement('span');
              b.className = 'pcd-dropdown__badge';
              b.textContent = self._formatPaise(appliedAmount) + ' OFF';
              right.insertBefore(b, right.firstChild);
            }
          }
        } else if (badge) {
          badge.remove();
        }

        // Data attr reflects the primary applied code (for backwards compat).
        if (appliedCode) couponDD.setAttribute('data-applied-code', appliedCode);
        else couponDD.removeAttribute('data-applied-code');

        // --- Reconcile offer rows with appliedList ----------------------
        // Build an index of existing rows by code.
        var rowsByCode = {};
        Array.prototype.forEach.call(couponDD.querySelectorAll('.pcd-offer'), function (row) {
          var codeAttr = row.getAttribute('data-applied-code');
          if (!codeAttr) {
            var applyB = row.querySelector('[data-pcd-apply-offer]');
            if (applyB) codeAttr = (applyB.getAttribute('data-code') || '').toUpperCase();
          }
          if (codeAttr) rowsByCode[codeAttr.toUpperCase()] = row;
        });

        function makeAppliedRow(code, amount, isAuto) {
          var row = document.createElement('div');
          row.className = 'pcd-offer is-applied';
          row.setAttribute('data-applied-code', code);
          row.innerHTML =
            '<div class="pcd-offer__badge">' + code + '</div>' +
            '<div class="pcd-offer__desc">' +
              (amount > 0
                ? '<strong>Applied</strong> · You saved ' + self._formatPaise(amount)
                : '<strong>Applied</strong>') +
            '</div>' +
            (isAuto ? '' :
              '<button type="button" class="pcd-offer__x" data-pcd-remove-coupon data-code="' + code + '" aria-label="Remove coupon">' +
                '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
              '</button>');
          return row;
        }

        function convertToApplied(row, code, amount, isAuto) {
          row.classList.add('is-applied');
          row.setAttribute('data-applied-code', code);
          var applyBtn = row.querySelector('[data-pcd-apply-offer]');
          if (applyBtn) applyBtn.remove();
          var desc = row.querySelector('.pcd-offer__desc');
          if (desc) {
            desc.innerHTML = amount > 0
              ? '<strong>Applied</strong> · You saved ' + self._formatPaise(amount)
              : '<strong>Applied</strong>';
          }
          var badgeEl = row.querySelector('.pcd-offer__badge');
          if (badgeEl) badgeEl.textContent = code;
          if (!isAuto && !row.querySelector('[data-pcd-remove-coupon]')) {
            var x = document.createElement('button');
            x.type = 'button';
            x.className = 'pcd-offer__x';
            x.setAttribute('data-pcd-remove-coupon', '');
            x.setAttribute('data-code', code);
            x.setAttribute('aria-label', 'Remove coupon');
            x.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
            row.appendChild(x);
          }
        }

        function convertToAvailable(row, code) {
          row.classList.remove('is-applied');
          row.removeAttribute('data-applied-code');
          var xBtn = row.querySelector('[data-pcd-remove-coupon]');
          if (xBtn) xBtn.remove();
          var desc = row.querySelector('.pcd-offer__desc');
          if (desc) desc.textContent = 'Apply this offer';
          if (!row.querySelector('[data-pcd-apply-offer]')) {
            var applyBtn = document.createElement('button');
            applyBtn.type = 'button';
            applyBtn.className = 'pcd-offer__btn';
            applyBtn.setAttribute('data-pcd-apply-offer', '');
            applyBtn.setAttribute('data-code', code);
            applyBtn.textContent = 'Apply';
            row.appendChild(applyBtn);
          }
        }

        // Pass 1: ensure every applied code has a row in applied state.
        appliedList.forEach(function (a) {
          var existing = rowsByCode[a.code];
          if (existing) {
            convertToApplied(existing, a.code, a.amount, a.isAuto);
          } else if (bodyInner) {
            var formEl = bodyInner.querySelector('[data-pcd-coupon-form]');
            var newRow = makeAppliedRow(a.code, a.amount, a.isAuto);
            if (formEl && formEl.nextSibling) bodyInner.insertBefore(newRow, formEl.nextSibling);
            else bodyInner.appendChild(newRow);
            rowsByCode[a.code] = newRow;
          }
        });

        // Pass 2: any row currently in applied state whose code is NOT in the
        // applied list must revert to available (or be removed if we don't
        // have a known code token for it).
        Array.prototype.forEach.call(couponDD.querySelectorAll('.pcd-offer.is-applied'), function (row) {
          var code = (row.getAttribute('data-applied-code') || '').toUpperCase();
          if (!code || !appliedSet[code]) {
            if (code) convertToAvailable(row, code);
            else row.remove();
          }
        });

        // Head subtitle.
        if (sub) {
          var n = appliedList.length;
          if (n === 1) {
            sub.innerHTML = 'Applied: <strong>' + appliedList[0].code + '</strong>';
          } else if (n > 1) {
            sub.textContent = n + ' coupons applied';
          } else {
            var offerCount = couponDD.querySelectorAll('.pcd-offer [data-pcd-apply-offer], .pcd-offer.pcd-offer--available').length ||
                             couponDD.querySelectorAll('.pcd-offer:not(.is-applied)').length;
            if (offerCount > 0) sub.textContent = offerCount + ' offer' + (offerCount > 1 ? 's' : '') + ' available';
            else sub.textContent = 'Have a code? Apply below';
          }
        }

        // Input stays editable so users can stack another code.
        var inputEl = couponDD.querySelector('.pcd-coupon-in');
        if (inputEl) {
          inputEl.removeAttribute('readonly');
          if (!appliedList.length) inputEl.value = '';
        }
      }

      // --- Auto-drop stale zero-allocation codes ----------------------
      if (!appliedCode && hasZeroCode) {
        self._clearDiscount().then(function () { self.refreshCart(); });
      }
    },

    // ---------- Refresh from section rendering API ----------------------
    // Uses a sequence counter so only the LATEST refresh ever writes DOM.
    // Older in-flight refreshes are discarded to avoid overwriting fresh
    // state with stale data (prevents "qty/total didn't update" bugs).
    _refreshSeq: 0,
    refreshCart: function () {
      var self = this;
      var seq = ++self._refreshSeq;
      var url = window.location.pathname + '?sections=' + SECTION_ID + '&_=' + Date.now();

      return fetch(url, {
        headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache' },
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Refresh failed: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (seq !== self._refreshSeq) return; // stale — a newer refresh is on its way
          var html = data[SECTION_ID];
          if (!html) throw new Error('No section HTML');
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newDrawer = doc.getElementById('pcd-drawer');
          if (!newDrawer) throw new Error('Drawer root not found in refresh');
          // Preserve scroll position across the innerHTML swap so the user
          // doesn't get snapped to the top on every cart mutation.
          var oldScrollEl = self.drawer.querySelector('#pcd-scroll');
          var savedScrollTop = oldScrollEl ? oldScrollEl.scrollTop : 0;
          self.drawer.innerHTML = newDrawer.innerHTML;
          self.scroll = document.getElementById('pcd-scroll');
          if (self.scroll && savedScrollTop) self.scroll.scrollTop = savedScrollTop;
          self.clearStaleCouponIfAny();
          self.shuffleUpsell();
        })
        .catch(function (err) { console.error('[PCD] refreshCart:', err); });
    },

    // ---------- Quantity change -----------------------------------------
    changeQty: function (btn) {
      var line = parseInt(btn.dataset.line, 10);
      var delta = parseInt(btn.dataset.pcdQty, 10); // +1 or -1
      var row = btn.closest('.pcd-cart-item');
      var key = row ? row.getAttribute('data-key') : null;
      var valEl = this.drawer.querySelector('[data-qty-val="' + line + '"]');
      if (!valEl) return;
      var current = parseInt(valEl.textContent, 10) || 0;
      var next = current + delta;

      if (next < 1) {
        var trash = this.drawer.querySelector('[data-pcd-remove][data-line="' + line + '"]');
        var title = trash ? (trash.dataset.productTitle || 'this item') : 'this item';
        return this.showRemoveNudge(line, title);
      }

      valEl.textContent = next;
      btn.disabled = true;

      var self = this;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-PCD-Own': '1' },
        credentials: 'same-origin',
        body: key
          ? JSON.stringify({ id: key, quantity: next })
          : JSON.stringify({ line: line, quantity: next })
      })
      .then(function (res) {
        if (!res.ok) throw new Error('change.js failed');
        return res.json();
      })
      .then(function (cart) {
        // Apply the /cart/change.js response immediately for instant UI feedback.
        // applyCartToDrawer already handles line prices, discount chip, applied→
        // available conversion, and auto-clearing stale zero-allocation codes —
        // so we do NOT trigger a full section refresh here. That extra refresh
        // was the root of the "snappy" flicker on qty change (innerHTML replace
        // resets scroll + re-renders every chip, even when nothing has changed).
        self.applyCartToDrawer(cart);
        self.updateCartBubble();
      })
      .catch(function (err) {
        valEl.textContent = current;
        console.error('[PCD] changeQty:', err);
        self.toast('Could not update quantity', 'error');
      })
      .then(function () { btn.disabled = false; });
    },

    // ---------- Remove nudge --------------------------------------------
    showRemoveNudge: function (line, title) {
      var self = this;
      var old = this.drawer.querySelector('.pcd-nudge-overlay');
      if (old) old.remove();

      var safeTitle = String(title || 'this item').replace(/[<>&"]/g, function (c) {
        return { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c];
      });

      var nudge = document.createElement('div');
      nudge.className = 'pcd-nudge-overlay';
      nudge.innerHTML =
        '<div class="pcd-nudge">' +
          '<div class="pcd-nudge__icon">⚠️</div>' +
          '<div class="pcd-nudge__title">Remove from cart?</div>' +
          '<div class="pcd-nudge__sub">Are you sure you want to remove ' + safeTitle + ' from your cart?</div>' +
          '<div class="pcd-nudge__actions">' +
            '<button type="button" class="pcd-nudge__btn pcd-nudge__btn--cancel">Keep it</button>' +
            '<button type="button" class="pcd-nudge__btn pcd-nudge__btn--confirm">Remove</button>' +
          '</div>' +
        '</div>';
      this.drawer.appendChild(nudge);
      requestAnimationFrame(function () { nudge.classList.add('show'); });

      nudge.querySelector('.pcd-nudge__btn--cancel').addEventListener('click', function () {
        nudge.classList.remove('show');
        setTimeout(function () { nudge.remove(); }, 300);
      });
      nudge.querySelector('.pcd-nudge__btn--confirm').addEventListener('click', function () {
        nudge.classList.remove('show');
        setTimeout(function () { nudge.remove(); }, 300);
        self.removeItem(line);
      });
    },

    removeItem: function (line) {
      var self = this;
      // Resolve the row now and capture its STABLE line_item_key. Using the
      // key (instead of a positional line number) is critical when multiple
      // removals are in-flight — line numbers shift as earlier items finish
      // removing, which used to cause the second click to hit the wrong item
      // (or a line that no longer exists), leaving some items stuck.
      var row = this.drawer.querySelector('.pcd-cart-item [data-pcd-remove][data-line="' + line + '"]');
      row = row ? row.closest('.pcd-cart-item') : null;
      var key = row ? row.getAttribute('data-key') : null;

      if (row) {
        var h = row.offsetHeight;
        row.style.maxHeight = h + 'px';
        void row.offsetHeight;
        row.classList.add('pcd-item--removing');
      }

      var doRemove = function () {
        // Prefer the stable key. Fall back to line number only if the row
        // didn't have one (shouldn't happen on current templates).
        var body = key
          ? JSON.stringify({ id: key, quantity: 0 })
          : JSON.stringify({ line: line, quantity: 0 });

        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-PCD-Own': '1' },
          credentials: 'same-origin',
          body: body
        })
        .then(function (res) {
          if (!res.ok) throw new Error('removeItem failed');
          return res.json();
        })
        .then(function (cart) {
          self.applyCartToDrawer(cart);
          self.updateCartBubble();

          // Remove THIS row (matched by key) and renumber the rest so qty
          // buttons' data-line stays in sync with the server ordering.
          if (row && row.parentNode) row.parentNode.removeChild(row);
          var remaining = self.drawer.querySelectorAll('.pcd-cart-item');
          remaining.forEach(function (el, idx) {
            var newLine = idx + 1;
            el.querySelectorAll('[data-line]').forEach(function (b) { b.setAttribute('data-line', newLine); });
            var qtyVal = el.querySelector('[data-qty-val]');
            if (qtyVal) qtyVal.setAttribute('data-qty-val', newLine);
          });

          if (cart.item_count === 0) self.refreshCart();
        })
        .catch(function (err) {
          console.error('[PCD] removeItem:', err);
          self.toast('Could not remove item', 'error');
        });
      };

      if (row) {
        return new Promise(function (resolve) {
          setTimeout(function () { doRemove().then(resolve); }, 280);
        });
      }
      return doRemove();
    },

    // ---------- Coupons --------------------------------------------------
    // Remove any applied discount code from the CART itself (not just the
    // cookie). Shopify's Ajax API accepts `discount: ""` on /cart/update.js
    // and this is what actually strips the code from cart.discount_applications
    // server-side. Clearing only the cookie leaves the code attached to the
    // cart, which is the root cause of "X doesn't remove HELLO".
    _clearDiscount: function () {
      var clearViaCart = fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-PCD-Own': '1'
        },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({ discount: '' })
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });

      return clearViaCart.then(function (cart) {
        // Belt-and-braces: nuke the discount_code cookie across every plausible
        // domain/path so it can't re-apply on the next page load.
        try {
          var host = location.hostname;
          var domains = ['', host, '.' + host];
          var parts = host.split('.');
          while (parts.length > 1) {
            parts.shift();
            domains.push('.' + parts.join('.'));
          }
          domains.forEach(function (d) {
            document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + (d ? '; domain=' + d : '');
          });
        } catch (e) { /* ignore */ }
        return cart;
      });
    },

    // Helper: list currently-applied MANUAL discount codes (not automatics)
    // from a cart JSON payload. Used so we can preserve existing codes when
    // applying a new one via /cart/update.js.
    _listManualCodes: function (cart) {
      var seen = {};
      var out = [];
      function add(title) {
        var u = (title || '').toUpperCase();
        if (!u || seen[u]) return;
        seen[u] = true;
        out.push(u);
      }
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        if (d.type === 'discount_code' && (d.total_allocated_amount || 0) > 0) add(d.title);
      });
      (cart.discount_applications || []).forEach(function (d) {
        if (d.type === 'discount_code' && (d.total_allocated_amount || 0) > 0) add(d.title);
      });
      return out;
    },

    _fetchCartJSON: function () {
      return fetch('/cart.js', {
        headers: { 'Accept': 'application/json', 'X-PCD-Own': '1' },
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (r) { return r.json(); });
    },

    // Apply a code by appending it to the existing manual-code set and
    // posting the combined list to /cart/update.js. This supports stacking
    // multiple manual codes on top of Shopify automatic discounts (when the
    // discounts are marked as combinable in admin). If Shopify accepts the
    // code but allocates 0 (e.g. min not met, not combinable) we roll back
    // to the prior code set.
    applyCoupon: function (code) {
      if (!code) return;
      var self = this;
      var upper = String(code).trim().toUpperCase();
      if (!upper) return;

      // Find the clicked row + its original description so we can ALWAYS
      // restore it (success, failure, or network error). This is what was
      // missing before — the rollback path left "Applying…" stuck.
      var couponDD = this.drawer && this.drawer.querySelector('[data-pcd-dropdown="coupon"]');
      var clickedRow = null;
      var originalDescHtml = '';
      var originalApplyBtnDisabled = false;

      if (couponDD) {
        Array.prototype.forEach.call(couponDD.querySelectorAll('.pcd-offer'), function (r) {
          var appliedAttr = (r.getAttribute('data-applied-code') || '').toUpperCase();
          var applyBtn = r.querySelector('[data-pcd-apply-offer]');
          var btnCode = applyBtn ? (applyBtn.getAttribute('data-code') || '').toUpperCase() : '';
          if (appliedAttr === upper || btnCode === upper) clickedRow = r;
        });
        if (clickedRow) {
          var descEl = clickedRow.querySelector('.pcd-offer__desc');
          if (descEl) originalDescHtml = descEl.innerHTML;
          var pendBtn = clickedRow.querySelector('[data-pcd-apply-offer]');
          if (pendBtn) {
            originalApplyBtnDisabled = pendBtn.disabled;
            pendBtn.disabled = true;
          }
          if (descEl) descEl.innerHTML = '<strong>Applying…</strong>';
        }
      }

      // Helpers to return the row to a clean state in any outcome.
      function restoreRow() {
        if (!clickedRow) return;
        var d = clickedRow.querySelector('.pcd-offer__desc');
        if (d) d.innerHTML = originalDescHtml || 'Apply this offer';
        var b = clickedRow.querySelector('[data-pcd-apply-offer]');
        if (b) b.disabled = originalApplyBtnDisabled;
      }
      function markRowApplied(amount) {
        if (!clickedRow) return;
        clickedRow.classList.add('is-applied');
        clickedRow.setAttribute('data-applied-code', upper);
        var d = clickedRow.querySelector('.pcd-offer__desc');
        if (d) {
          d.innerHTML = amount > 0
            ? '<strong>Applied</strong> · You saved ' + self._formatPaise(amount)
            : '<strong>Applied</strong>';
        }
        var b = clickedRow.querySelector('[data-pcd-apply-offer]');
        if (b) b.remove();
        if (!clickedRow.querySelector('[data-pcd-remove-coupon]')) {
          var x = document.createElement('button');
          x.type = 'button';
          x.className = 'pcd-offer__x';
          x.setAttribute('data-pcd-remove-coupon', '');
          x.setAttribute('data-code', upper);
          x.setAttribute('aria-label', 'Remove coupon');
          x.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
          clickedRow.appendChild(x);
        }
      }

      this._fetchCartJSON().then(function (cart) {
        var existing = self._listManualCodes(cart);
        var already = existing.indexOf(upper) !== -1;
        var next = already ? existing : existing.concat([upper]);

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-PCD-Own': '1'
          },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({ discount: next.join(',') })
        })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            if (!res.ok) {
              var msg = (body && (body.description || body.message)) || ('HTTP ' + res.status);
              throw new Error(msg);
            }
            return body;
          });
        })
        .then(function (updated) {
          // Three outcomes for the just-POSTed code:
          //   A. Shopify listed it with allocation > 0 → definitive success
          //   B. Shopify listed it with allocation = 0 → code was accepted into
          //      session but current cart doesn't meet min / isn't eligible yet.
          //      We treat this as "applied (pending)" — the code stays in
          //      session and will kick in as soon as the cart qualifies. Only
          //      rollback if the user explicitly removes it.
          //   C. Shopify didn't list the code at all → code is invalid / expired.
          //      This is the only real rejection case.
          var effective = 0;
          var codeListed = false;
          (updated.cart_level_discount_applications || []).forEach(function (d) {
            if ((d.title || '').toUpperCase() === upper) {
              codeListed = true;
              effective += (d.total_allocated_amount || 0);
            }
          });
          (updated.discount_applications || []).forEach(function (d) {
            if (d.type === 'discount_code' && (d.title || '').toUpperCase() === upper) {
              codeListed = true;
              effective += (d.total_allocated_amount || 0);
            }
          });

          if (codeListed) {
            // A or B — code is in session. Mark applied.
            markRowApplied(effective);
            self.applyCartToDrawer(updated);
            self.updateCartBubble();
            if (effective > 0) {
              self.toast(upper + ' applied', 'success');
            } else {
              // Pending — tell user it'll kick in later.
              self.toast(upper + ' saved — will apply when cart qualifies', 'info');
            }
            self.refreshCart();
          } else {
            // C — Shopify rejected outright. Restore UI + rollback.
            restoreRow();
            self.toast(upper + ' is not a valid code', 'error');

            fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-PCD-Own': '1'
              },
              credentials: 'same-origin',
              cache: 'no-store',
              body: JSON.stringify({ discount: existing.join(',') })
            })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (rolled) {
              if (rolled) { self.applyCartToDrawer(rolled); self.updateCartBubble(); }
              self.refreshCart();
            }).catch(function () { self.refreshCart(); });
          }
        });
      })
      .catch(function (err) {
        console.error('[PCD] applyCoupon:', err);
        // CRITICAL: restore the row so it doesn't stay stuck.
        restoreRow();
        var msg = (err && err.message) ? err.message : 'Could not apply coupon';
        self.toast(msg, 'error');
        self.refreshCart();
      });
    },

    // Shuffle the Offers Unlocked carousel with a per-session seed that
    // persists for ~10 minutes. Same user gets the SAME order across repeated
    // drawer opens within the TTL — so clicking a product and returning
    // doesn't re-scramble the list. After 10 min a fresh seed is generated.
    shuffleUpsell: function () {
      if (!this.drawer) return;
      var carousel = this.drawer.querySelector('.pcd-upsell__carousel');
      if (!carousel) { this.stopUpsellAutoScroll(); return; }
      var cards = Array.prototype.slice.call(carousel.querySelectorAll('.pcd-up-card'));
      if (cards.length < 2) { this.startUpsellAutoScroll(); return; }

      var SEED_KEY = 'pcd-upsell-seed';
      var TS_KEY = 'pcd-upsell-seed-ts';
      var TTL = 10 * 60 * 1000;
      var seed;
      try {
        var stored = sessionStorage.getItem(SEED_KEY);
        var ts = parseInt(sessionStorage.getItem(TS_KEY), 10);
        if (stored && ts && (Date.now() - ts) < TTL) {
          seed = parseInt(stored, 10) >>> 0;
        } else {
          seed = (Math.floor(Math.random() * 0xFFFFFFFF)) >>> 0;
          sessionStorage.setItem(SEED_KEY, String(seed));
          sessionStorage.setItem(TS_KEY, String(Date.now()));
        }
      } catch (e) {
        seed = (Math.floor(Math.random() * 0xFFFFFFFF)) >>> 0;
      }

      function prng() {
        seed = (seed + 0x6D2B79F5) >>> 0;
        var t = seed;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      }

      for (var i = cards.length - 1; i > 0; i--) {
        var j = Math.floor(prng() * (i + 1));
        var tmp = cards[i]; cards[i] = cards[j]; cards[j] = tmp;
      }
      var frag = document.createDocumentFragment();
      cards.forEach(function (el) { frag.appendChild(el); });
      carousel.appendChild(frag);

      this.startUpsellAutoScroll();
    },

    // Slow auto-scrolling carousel for the Offers Unlocked row. Pauses while
    // the user hovers/touches it (so it doesn't fight manual scrolling) and
    // smoothly loops back to the start when it reaches the end.
    _upsellScrollTimer: null,
    _upsellUnbind: null,
    startUpsellAutoScroll: function () {
      var self = this;
      this.stopUpsellAutoScroll();
      var carousel = this.drawer && this.drawer.querySelector('.pcd-upsell__carousel');
      if (!carousel) return;
      if (carousel.scrollWidth <= carousel.clientWidth + 4) return;

      var paused = false;
      var pause = function () { paused = true; };
      var resume = function () { paused = false; };
      carousel.addEventListener('mouseenter', pause);
      carousel.addEventListener('mouseleave', resume);
      carousel.addEventListener('touchstart', pause, { passive: true });
      carousel.addEventListener('touchend', resume, { passive: true });
      carousel.addEventListener('pointerdown', pause);
      carousel.addEventListener('pointerup', resume);
      this._upsellUnbind = function () {
        carousel.removeEventListener('mouseenter', pause);
        carousel.removeEventListener('mouseleave', resume);
        carousel.removeEventListener('touchstart', pause);
        carousel.removeEventListener('touchend', resume);
        carousel.removeEventListener('pointerdown', pause);
        carousel.removeEventListener('pointerup', resume);
      };

      var PX_PER_TICK = 0.5;
      var TICK_MS = 30;
      this._upsellScrollTimer = setInterval(function () {
        if (!document.body.contains(carousel)) { self.stopUpsellAutoScroll(); return; }
        if (!self.isOpen()) return;
        if (paused) return;
        var max = carousel.scrollWidth - carousel.clientWidth;
        if (max <= 0) return;
        if (carousel.scrollLeft >= max - 1) {
          carousel.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          carousel.scrollLeft += PX_PER_TICK;
        }
      }, TICK_MS);
    },
    stopUpsellAutoScroll: function () {
      if (this._upsellScrollTimer) {
        clearInterval(this._upsellScrollTimer);
        this._upsellScrollTimer = null;
      }
      if (this._upsellUnbind) { this._upsellUnbind(); this._upsellUnbind = null; }
    },

    // Stale = cookie set & Shopify lists the code in discount_applications
    // but allocates 0 (e.g. the cart dropped below the code's min).
    clearStaleCouponIfAny: function () {
      var el = this.drawer && this.drawer.querySelector('[data-pcd-stale-code]');
      if (!el) return;
      el.removeAttribute('data-pcd-stale-code');
      var self = this;
      this._clearDiscount().then(function () { self.refreshCart(); });
    },

    // User-triggered: the X on an applied-code chip. When a specific code is
    // passed we remove ONLY that code, preserving any other manually-applied
    // codes. Called with no arg from older code paths → full clear.
    removeCoupon: function (code) {
      var self = this;
      var upper = (code || '').toString().toUpperCase();

      if (!upper) {
        return this._clearDiscount().then(function (cart) {
          if (cart) { self.applyCartToDrawer(cart); self.updateCartBubble(); }
          self.refreshCart();
          self.toast('Coupon removed', 'info');
        });
      }

      // Capture the row's pre-removal state so we can restore on failure.
      var row = this.drawer && this.drawer.querySelector('.pcd-offer.is-applied[data-applied-code="' + upper + '"]');
      var originalRowHtml = row ? row.outerHTML : '';

      // Optimistic UI: flip the row back to available immediately.
      if (row) {
        row.classList.remove('is-applied');
        var xBtn = row.querySelector('[data-pcd-remove-coupon]');
        if (xBtn) xBtn.remove();
        var desc = row.querySelector('.pcd-offer__desc');
        if (desc) desc.textContent = 'Apply this offer';
        row.removeAttribute('data-applied-code');
        if (!row.querySelector('[data-pcd-apply-offer]')) {
          var applyBtn = document.createElement('button');
          applyBtn.type = 'button';
          applyBtn.className = 'pcd-offer__btn';
          applyBtn.setAttribute('data-pcd-apply-offer', '');
          applyBtn.setAttribute('data-code', upper);
          applyBtn.textContent = 'Apply';
          row.appendChild(applyBtn);
        }
      }

      function restoreApplied() {
        if (!row || !originalRowHtml) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = originalRowHtml;
        var restored = tmp.firstElementChild;
        if (restored && row.parentNode) row.parentNode.replaceChild(restored, row);
      }

      this._fetchCartJSON().then(function (cart) {
        var existing = self._listManualCodes(cart);
        var next = existing.filter(function (c) { return c !== upper; });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-PCD-Own': '1'
          },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({ discount: next.join(',') })
        })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (updated) {
          if (updated) { self.applyCartToDrawer(updated); self.updateCartBubble(); }
          if (!next.length) {
            try {
              var host = location.hostname;
              var domains = ['', host, '.' + host];
              var parts = host.split('.');
              while (parts.length > 1) { parts.shift(); domains.push('.' + parts.join('.')); }
              domains.forEach(function (d) {
                document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + (d ? '; domain=' + d : '');
              });
            } catch (e) { /* ignore */ }
          }
          self.refreshCart();
          self.toast(upper + ' removed', 'info');
        });
      })
      .catch(function (err) {
        console.error('[PCD] removeCoupon:', err);
        // Restore the row to its applied state since removal failed.
        restoreApplied();
        self.toast('Could not remove coupon', 'error');
        self.refreshCart();
      });
    },

    // ---------- Upsell --------------------------------------------------
    addUpsell: function (variantId, btn) {
      if (!variantId) return;
      var self = this;
      var id = parseInt(variantId, 10);
      if (btn) btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-PCD-Own': '1' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }], sections: SECTION_ID })
      })
      .then(function (res) {
        if (!res.ok) throw new Error('upsell add failed');
        return res.json();
      })
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION_ID]) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(data.sections[SECTION_ID], 'text/html');
          var newDrawer = doc.getElementById('pcd-drawer');
          if (newDrawer) {
            self.drawer.innerHTML = newDrawer.innerHTML;
            self.scroll = document.getElementById('pcd-scroll');
          }
        }
        self.clearStaleCouponIfAny();
        self.shuffleUpsell();
        self.updateCartBubble();
        self.highlightItem(id);
      })
      .catch(function (err) {
        console.error('[PCD] addUpsell:', err);
        self.toast('Could not add to cart', 'error');
      })
      .then(function () { if (btn) btn.disabled = false; });
    },

    // ---------- Dropdowns -----------------------------------------------
    toggleDropdown: function (head) {
      var section = head.closest('[data-pcd-dropdown]');
      if (section) section.classList.toggle('is-open');
    },

    // ---------- Checkout ------------------------------------------------
    // This store exposes a global `onCheckoutClick(button)` (see gokwik-button.liquid).
    // Call it directly, no loader/disabled state. Never fall back to Shopify native.
    proceedToCheckout: function () {
      var btn = this.drawer.querySelector('[data-pcd-checkout]');
      if (!btn || btn.disabled) return;

      // Don't initiate GoKwik on an empty cart — guard against the race where
      // a user has just removed the last item and the drawer DOM hasn't
      // finished updating yet.
      var count = 0;
      var bubble = this.drawer.querySelector('.pcd-header__title small');
      if (bubble) {
        var m = (bubble.textContent || '').match(/\d+/);
        if (m) count = parseInt(m[0], 10);
      }
      if (count <= 0 || this.drawer.querySelector('.pcd-empty')) {
        this.toast('Your cart is empty', 'error');
        return;
      }

      // GoKwik's SDK mutates the button we pass to it (swaps innerHTML to a loader,
      // adds width/disabled styles). When the modal is closed those mutations stay
      // on our button and it appears "shrunk". Snapshot the button and restore it.
      var self = this;
      var snap = {
        html: btn.innerHTML,
        className: btn.className,
        style: btn.getAttribute('style') || '',
        disabled: btn.disabled,
        width: btn.style.width,
        minWidth: btn.style.minWidth,
        maxWidth: btn.style.maxWidth
      };
      var restored = false;
      function restoreBtn() {
        if (restored) return;
        restored = true;
        var live = self.drawer && self.drawer.querySelector('[data-pcd-checkout]');
        if (!live) return;
        live.innerHTML = snap.html;
        live.className = snap.className;
        if (snap.style) live.setAttribute('style', snap.style);
        else live.removeAttribute('style');
        live.disabled = snap.disabled;
      }
      // Restore when GoKwik signals close, when user returns to the tab, or
      // after a short fallback delay so the button never stays mutated.
      window.addEventListener('gk-checkout-close', restoreBtn, { once: true });
      document.addEventListener('visibilitychange', function onVis() {
        if (document.visibilityState === 'visible') {
          restoreBtn();
          document.removeEventListener('visibilitychange', onVis);
        }
      });
      setTimeout(restoreBtn, 4000);

      if (typeof window.onCheckoutClick === 'function') {
        try { window.onCheckoutClick(btn); return; } catch (e) { console.error('[PCD] onCheckoutClick threw:', e); }
      }
      if (window.gokwikSdk && typeof window.gokwikSdk.initCheckout === 'function') {
        try { window.gokwikSdk.initCheckout(); return; } catch (e) { console.error('[PCD] gokwikSdk.initCheckout threw:', e); }
      }
      restoreBtn();
      console.warn('[PCD] GoKwik not ready');
      this.toast('Checkout is loading, please try again', 'error');
    },

    // ---------- Header bubble count ------------------------------------
    updateCartBubble: function () {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart.item_count;
          document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble, .cart-bubble').forEach(function (el) {
            el.textContent = count;
            el.classList.toggle('is-empty', count === 0);
            if (el.hasAttribute('data-cart-count')) el.setAttribute('data-cart-count', count);
          });
        })
        .catch(function () { /* non-critical */ });
    },

    // ---------- Toast ---------------------------------------------------
    toast: function (msg, type) {
      type = type || 'info';
      var t = document.createElement('div');
      t.className = 'pcd-toast pcd-toast--' + type;
      t.textContent = msg;
      this.drawer.appendChild(t);
      requestAnimationFrame(function () { t.classList.add('show'); });
      setTimeout(function () {
        t.classList.remove('show');
        setTimeout(function () { t.remove(); }, 300);
      }, 2500);
    }
  };

  // ---------- Public API aliases expected by other theme sections ----------
  // Bundle & Save, Sticky Cart, and Add-to-Cart PRO+ all call these names.
  PCD.refresh = function () { return PCD.refreshCart(); };
  PCD.open    = function (opts) { return PCD.openDrawer(opts); };
  PCD.close   = function () { return PCD.closeDrawer(); };

  // ────────────────────────────────────────────────────────────────
  // Fast-add path — NO server section render.
  //
  // Instead of waiting for Shopify's slow ?sections=premium-cart-drawer
  // render (1–3s on complex themes), we:
  //   1. POST /cart/add.js with NO sections parameter (~200–400ms)
  //   2. Synthesize a line-item row from the response and inject it
  //      into the drawer DOM directly (instant)
  //   3. In the background, run PCD.refreshCart() for full section
  //      consistency (coupons, upsell, etc.) — non-blocking
  // ────────────────────────────────────────────────────────────────
  function _escape(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function _formatPrice(paise) {
    if (typeof PCD._formatPaise === 'function') return PCD._formatPaise(paise);
    var rupees = Math.round((paise || 0) / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function _buildCartItemHTML(item, lineNumber) {
    var key = _escape(item.key || '');
    var vid = _escape(item.variant_id || item.id || '');
    var unitPrice = item.price || 0;
    var compareAt = item.variant && item.variant.compare_at_price ? item.variant.compare_at_price : 0;
    var unitMRP = (compareAt && compareAt > (item.variant && item.variant.price || unitPrice)) ? compareAt : 0;
    var title = _escape(item.product_title || item.title || '');
    var variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? _escape(item.variant_title) : '';
    var image = item.image || item.featured_image && item.featured_image.url || '';
    var linePrice = item.final_line_price || unitPrice;
    var lineMRP = unitMRP * item.quantity;
    var hasMRP = lineMRP > linePrice;
    var pct = hasMRP ? Math.round((lineMRP - linePrice) * 100 / lineMRP) : 0;
    var url = _escape(item.url || '');

    return '' +
      '<div class="pcd-cart-item" ' +
           'data-variant-id="' + vid + '" ' +
           'data-key="' + key + '" ' +
           'data-unit-price="' + unitPrice + '" ' +
           'data-unit-mrp="' + (unitMRP || 0) + '">' +
        (image
          ? '<img class="pcd-cart-item__img" src="' + _escape(image) + '" alt="' + title + '" loading="lazy" width="74" height="74">'
          : '<div class="pcd-cart-item__img pcd-cart-item__img--placeholder" aria-hidden="true"></div>') +
        '<div class="pcd-cart-item__body">' +
          '<div class="pcd-cart-item__top">' +
            '<div class="pcd-cart-item__info">' +
              '<a href="' + url + '" class="pcd-cart-item__title">' + title + '</a>' +
              (variantTitle ? '<div class="pcd-cart-item__variant">' + variantTitle + '</div>' : '') +
            '</div>' +
            '<div class="pcd-cart-item__price-stack">' +
              '<div class="pcd-cart-item__price-row">' +
                '<span class="pcd-cart-item__price">' + _formatPrice(linePrice) + '</span>' +
                (hasMRP ? '<span class="pcd-cart-item__mrp">' + _formatPrice(lineMRP) + '</span>' : '') +
              '</div>' +
              (hasMRP ? '<div class="pcd-cart-item__save">' + pct + '% off</div>' : '') +
            '</div>' +
          '</div>' +
          '<div class="pcd-cart-item__bottom">' +
            '<div class="pcd-qty" role="group" aria-label="Quantity">' +
              '<button type="button" class="pcd-qty__btn" data-pcd-qty="-1" data-line="' + lineNumber + '" aria-label="Decrease quantity">−</button>' +
              '<div class="pcd-qty__val" data-qty-val="' + lineNumber + '">' + (item.quantity || 1) + '</div>' +
              '<button type="button" class="pcd-qty__btn" data-pcd-qty="1" data-line="' + lineNumber + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="pcd-trash-btn" data-pcd-remove data-line="' + lineNumber + '" data-product-title="' + title + '" aria-label="Remove ' + title + '">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
                '<polyline points="3 6 5 6 21 6"/>' +
                '<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>' +
              '</svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  // Insert (or merge-quantity on) a single line item into the drawer DOM.
  function _injectOptimisticItem(item) {
    var drawer = PCD.drawer;
    if (!drawer || !item) return;

    var cartSection = drawer.querySelector('.pcd-cart-section');

    // If drawer is currently showing empty state, replace with a fresh cart-section
    var emptyEl = drawer.querySelector('.pcd-empty');
    if (emptyEl && emptyEl.parentNode) {
      var newSection = document.createElement('div');
      newSection.className = 'pcd-cart-section';
      emptyEl.parentNode.replaceChild(newSection, emptyEl);
      cartSection = newSection;
    }
    if (!cartSection) return;

    var existingRows = cartSection.querySelectorAll('.pcd-cart-item');
    var matchKey = item.key;

    // Same key already present? Just bump the quantity.
    for (var i = 0; i < existingRows.length; i++) {
      if (existingRows[i].getAttribute('data-key') === matchKey) {
        var qtyVal = existingRows[i].querySelector('.pcd-qty__val');
        if (qtyVal) qtyVal.textContent = item.quantity;
        return;
      }
    }

    // New row — prepend so user sees it at the top.
    var lineNumber = existingRows.length + 1;
    var html = _buildCartItemHTML(item, lineNumber);
    cartSection.insertAdjacentHTML('afterbegin', html);

    // Renumber everyone so qty/remove buttons map to correct server lines.
    var allRows = cartSection.querySelectorAll('.pcd-cart-item');
    allRows.forEach(function (el, idx) {
      var n = idx + 1;
      el.querySelectorAll('[data-line]').forEach(function (b) { b.setAttribute('data-line', n); });
      var qv = el.querySelector('[data-qty-val]');
      if (qv) qv.setAttribute('data-qty-val', n);
    });

    // Update header count small
    var headerSmall = drawer.querySelector('.pcd-header__title small');
    if (headerSmall) {
      // we don't know the exact total from /cart/add.js single-item response,
      // so increment visible count by this item's quantity
      var m = (headerSmall.textContent || '').match(/\d+/);
      var cur = m ? parseInt(m[0], 10) : 0;
      headerSmall.textContent = '(' + (cur + (item.quantity || 1)) + ')';
    }
  }

  // Apply section HTML returned by Shopify's /cart/add.js ?sections= response.
  // This is atomic: the HTML comes from the same request that committed the
  // mutation, so there is no race.
  function _applySectionHTML(html) {
    if (!html || !PCD.drawer) return;
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var newDrawer = doc.getElementById('pcd-drawer');
    if (!newDrawer) return;
    PCD.drawer.innerHTML = newDrawer.innerHTML;
    PCD.scroll = document.getElementById('pcd-scroll');
    PCD.clearStaleCouponIfAny();
    PCD.shuffleUpsell();
  }

  PCD.addItem = function (variantId, quantity) {
    var id = parseInt(variantId, 10);
    var qty = parseInt(quantity, 10) || 1;
    if (!id) return Promise.reject(new Error('addItem: missing variantId'));

    var drawerOpened = false;
    var openDrawerIfNeeded = function () {
      if (drawerOpened) return;
      drawerOpened = true;
      PCD.openDrawer({ skipRefresh: true });
    };
    // 80ms race: if POST resolves faster, we open the drawer with the new
    // item already inside. If slower, drawer opens anyway at 80ms.
    setTimeout(openDrawerIfNeeded, 80);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-PCD-Own': '1' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ id: id, quantity: qty, sections: SECTION_ID })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; }, function () { throw new Error('add failed'); });
        return r.json();
      })
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION_ID]) _applySectionHTML(data.sections[SECTION_ID]);
        PCD.updateCartBubble();
        openDrawerIfNeeded();
        PCD.highlightItem(id);
      })
      .catch(function (err) {
        PCD.closeDrawer();
        throw err;
      });
  };

  PCD.addBundleItems = function (items) {
    var payload = (items || [])
      .map(function (it) { return { id: parseInt(it.id, 10), quantity: parseInt(it.quantity, 10) || 1 }; })
      .filter(function (it) { return !!it.id; });
    if (!payload.length) return Promise.reject(new Error('addBundleItems: no items'));

    var drawerOpened = false;
    var openDrawerIfNeeded = function () {
      if (drawerOpened) return;
      drawerOpened = true;
      PCD.openDrawer({ skipRefresh: true });
    };
    setTimeout(openDrawerIfNeeded, 80);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-PCD-Own': '1' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ items: payload, sections: SECTION_ID })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; }, function () { throw new Error('bundle add failed'); });
        return r.json();
      })
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION_ID]) _applySectionHTML(data.sections[SECTION_ID]);
        PCD.updateCartBubble();
        openDrawerIfNeeded();
        PCD.highlightItem(payload[0].id);
      })
      .catch(function (err) {
        PCD.closeDrawer();
        throw err;
      });
  };

  window.PCD = PCD;
  window.cartDrawer = PCD; // theme-section alias (see bundle-save, sticky-cart, atcpro)

  // ---------- Site-wide interception (capture phase, before theme.js) ----
  // If we land directly on /cart, bounce back and open drawer after nav.
  (function earlyRedirect() {
    try {
      if (window.location.pathname === '/cart') {
        var ref = '';
        try {
          if (document.referrer) {
            var u = new URL(document.referrer);
            if (u.origin === window.location.origin && u.pathname.replace(/\/+$/, '') !== '/cart') {
              ref = u.pathname + u.search + u.hash;
            }
          }
        } catch (e) { /* ignore */ }
        if (!ref) ref = '/';
        sessionStorage.setItem('pcd-open-on-load', '1');
        window.location.replace(ref);
      }
    } catch (e) { /* ignore */ }
  })();

  // Intercept ALL add-to-cart form submits BEFORE theme.js gets them.
  // Strategy for instant feel:
  //   1. Flip button to "✓ Added" in the same frame as the click.
  //   2. Open the drawer IMMEDIATELY (with current cart state) — zero wait.
  //   3. POST /cart/add.js?sections= in the background.
  //   4. When response lands, update the drawer's innerHTML in place so the
  //      new item pops into the already-open drawer.
  //   5. On failure, revert the button and toast the error.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();
    e.stopPropagation();

    var btn = form.querySelector('[type="submit"], [name="add"]');
    if (btn && !btn.dataset.originalHtml) {
      btn.dataset.originalHtml = btn.innerHTML;
    }
    if (btn) {
      // OPTIMISTIC — full innerHTML swap ensures text is always visible,
      // regardless of the theme's multi-span button structure.
      btn.innerHTML = '<span>✓ Added</span>';
      btn.classList.add('pcd-atc-added');
      btn.disabled = false;
    }

    // RACE: give the POST ~80ms to land before opening the drawer.
    // If the network is fast, drawer opens with the NEW item already inside.
    // If slow, drawer still opens quickly (80ms) with current state and
    // updates in place when the response arrives.
    var drawerOpened = false;
    var openDrawerIfNeeded = function () {
      if (drawerOpened) return;
      drawerOpened = true;
      PCD.openDrawer({ skipRefresh: true });
    };
    setTimeout(openDrawerIfNeeded, 80);

    var formData = new FormData(form);
    formData.append('sections', SECTION_ID);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-PCD-Own': '1' },
      credentials: 'same-origin',
      body: formData
    })
    .then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw j; }, function () { throw new Error('Add failed'); });
      return res.json();
    })
    .then(function (added) {
      var vid = added.variant_id || (added.items && added.items[0] && added.items[0].variant_id);
      if (added && added.sections && added.sections[SECTION_ID]) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(added.sections[SECTION_ID], 'text/html');
        var newDrawer = doc.getElementById('pcd-drawer');
        if (newDrawer) {
          PCD.drawer.innerHTML = newDrawer.innerHTML;
          PCD.scroll = document.getElementById('pcd-scroll');
        }
      }
      PCD.clearStaleCouponIfAny();
      PCD.shuffleUpsell();
      PCD.updateCartBubble();
      openDrawerIfNeeded();
      if (vid != null) PCD.highlightItem(vid);
    })
    .catch(function (err) {
      console.error('[PCD] Add to cart failed:', err);
      var msg = (err && err.description) || 'Could not add to cart. Try again.';
      if (PCD.drawer) PCD.toast(msg, 'error');
      // Revert optimistic UI only on failure.
      if (btn) {
        btn.classList.remove('pcd-atc-added');
        if (btn.dataset.originalHtml) {
          btn.innerHTML = btn.dataset.originalHtml;
          delete btn.dataset.originalHtml;
        }
        btn.disabled = false;
      }
      // Close drawer since the add never happened.
      PCD.closeDrawer();
    });
  }, true);

  // Intercept clicks on cart links — open drawer instead of navigating.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href="/cart"], a[href^="/cart?"], a[href$="/cart"]');
    if (!link) return;
    // Allow explicit opt-out with data-pcd-ignore
    if (link.hasAttribute('data-pcd-ignore')) return;
    e.preventDefault();
    e.stopPropagation();
    PCD.openDrawer();
  }, true);

  // Optional GoKwik lifecycle hooks
  window.addEventListener('gk-checkout-close', function () {
    PCD.refreshCart();
  });

  // ---------- Global cart-mutation detection ---------------------------
  // Many widgets on this theme call /cart/add.js or /cart/change.js directly via
  // fetch or XHR (bundle offers, sticky cart, quick-add, etc.). Patch both so
  // our drawer always knows when the cart changes, and opens on add-to-cart.
  var _cartMutateRE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  function _isOwnRefresh(url) {
    return /sections=premium-cart-drawer/.test(url);
  }

  function _isOwnFetch(init) {
    try {
      var h = init && init.headers;
      if (!h) return false;
      if (typeof h.get === 'function') return h.get('X-PCD-Own') === '1';
      return h['X-PCD-Own'] === '1' || h['x-pcd-own'] === '1';
    } catch (e) { return false; }
  }

  // Simple post-mutation refresh for third-party cart widgets we can't
  // rewrite. Refresh section, update bubble, open drawer on add.
  function _postMutationRefresh(isAdd) {
    PCD.refreshCart().then(function () {
      PCD.updateCartBubble();
      if (isAdd) PCD.openDrawer();
    });
  }

  var _origFetch = window.fetch;
  if (typeof _origFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var p = _origFetch.apply(this, arguments);
      if (_isOwnFetch(init)) return p;
      if (_cartMutateRE.test(url) && method !== 'GET' && !_isOwnRefresh(url)) {
        p.then(function (res) {
          // Only refresh/open on success — skip 4xx/5xx so drawer doesn't
          // pop up empty when an add actually failed (unavailable variant etc).
          if (res && res.ok) _postMutationRefresh(/\/cart\/add/.test(url));
          return res;
        }).catch(function () {});
      }
      return p;
    };
  }

  (function patchXHR() {
    if (!window.XMLHttpRequest) return;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    var origSetHeader = XMLHttpRequest.prototype.setRequestHeader;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__pcd_url = url;
      this.__pcd_method = (method || 'GET').toUpperCase();
      this.__pcd_own = false;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
      if (String(name).toLowerCase() === 'x-pcd-own' && String(value) === '1') this.__pcd_own = true;
      return origSetHeader.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      var xhr = this;
      if (!xhr.__pcd_own && xhr.__pcd_url && _cartMutateRE.test(xhr.__pcd_url) && xhr.__pcd_method !== 'GET' && !_isOwnRefresh(xhr.__pcd_url)) {
        var isAdd = /\/cart\/add/.test(xhr.__pcd_url);
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 400) _postMutationRefresh(isAdd);
        });
      }
      return origSend.apply(this, arguments);
    };
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { PCD.init(); });
  } else {
    PCD.init();
  }
})();
